import { useState, useEffect, useCallback } from "react";
import {
  ArrowLeft,
  Heart,
  Droplets,
  Footprints,
  ClipboardList,
  MessageSquare,
  Phone,
  Calendar,
  Moon,
  Smile,
  Stethoscope,
  AlertTriangle,
  CheckCircle,
  ShieldAlert,
  User,
  Users,
  Droplet,
  Ruler,
  Weight,
  Building2,
  TrendingUp,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { Avatar, Card, SectionTitle } from "../components/UI";
import { PageLoader } from "../components/LoadingSpinner";
import { ErrorAlert, InlineError } from "../components/ErrorAlert";
import usersApi from "../api/users";
import CsqResults from "./CsqResults";

// ── Helpers ───────────────────────────────────────────────────────────────────
const RISK_LEVEL_MAP = {
  low: {
    label: "Low Risk",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
    barColor: "bg-green-500",
    icon: CheckCircle,
  },
  moderate: {
    label: "Moderate Risk",
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
    barColor: "bg-yellow-400",
    icon: AlertTriangle,
  },
  high: {
    label: "High Risk",
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
    barColor: "bg-red-500",
    icon: ShieldAlert,
  },
};

const PRIORITY_COLORS = {
  high: "bg-red-100 text-red-700 border-red-200",
  medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
  low: "bg-blue-100 text-blue-700 border-blue-200",
};

// Sesuai "Daily mood tracker" di kuesioner
const MOOD_MAP = {
  sangat_bahagia: {
    emoji: "😊",
    label: "Sangat Bahagia",
    color: "text-green-600",
    bg: "bg-green-50",
    border: "border-green-200",
  },
  bahagia: {
    emoji: "🙂",
    label: "Bahagia",
    color: "text-blue-600",
    bg: "bg-blue-50",
    border: "border-blue-200",
  },
  biasa_saja: {
    emoji: "😐",
    label: "Biasa Saja",
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    border: "border-yellow-200",
  },
  sedih: {
    emoji: "😔",
    label: "Sedih",
    color: "text-orange-600",
    bg: "bg-orange-50",
    border: "border-orange-200",
  },
  sangat_sedih: {
    emoji: "😭",
    label: "Sangat Sedih",
    color: "text-red-600",
    bg: "bg-red-50",
    border: "border-red-200",
  },
};

const QUALITY_MAP = {
  0: { label: "Nyenyak", color: "text-green-600" },
  1: { label: "Cukup", color: "text-yellow-600" },
  2: { label: "Buruk", color: "text-red-600" },
};

const WAKEUP_MAP = {
  0: "0–2 kali",
  1: "3–4 kali",
  2: "≥ 5 kali",
};

const SLEEP_CATEGORY_COLOR = {
  "Tidur Baik": "text-green-600",
  "Tidur Terganggu": "text-yellow-600",
  "Tidur Buruk": "text-red-600",
};

// Group health data by date untuk chart
function groupHealthByDate(data) {
  const grouped = {};
  data.forEach((item) => {
    const date = new Date(item.recorded_at).toLocaleDateString("id-ID", {
      day: "2-digit",
      month: "short",
    });
    if (!grouped[date]) {
      grouped[date] = { date, bpm: [], systolic: [], diastolic: [], steps: [] };
    }
    grouped[date].bpm.push(item.bpm);
    grouped[date].systolic.push(item.systolic);
    grouped[date].diastolic.push(item.diastolic);
    grouped[date].steps.push(item.steps);
  });

  // *** DIUBAH 2026-09-17: smartwatch/sync sekarang nulis 1 sensor per baris
  // (lihat SmartwatchController@sync), jadi array bpm/systolic/diastolic/
  // steps per tanggal bisa berisi banyak `null` (baris yang isinya sensor
  // lain). null + angka tetap jalan di JS (null dianggap 0) TAPI itu bikin
  // rata-rata BPM/tekanan darah jadi salah/terlalu rendah kalau ikut
  // dihitung. Filter null dulu sebelum di-rata-rata; steps tetap boleh
  // dijumlah apa adanya karena itu total, bukan rata-rata. ***
  const avg = (arr) => {
    const valid = arr.filter((v) => v !== null && v !== undefined);
    if (valid.length === 0) return null;
    return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
  };
  const sum = (arr) =>
    arr.filter((v) => v !== null && v !== undefined).reduce((a, b) => a + b, 0);

  return Object.values(grouped)
    .map((d) => ({
      date: d.date,
      bpm: avg(d.bpm),
      systolic: avg(d.systolic),
      diastolic: avg(d.diastolic),
      steps: sum(d.steps),
    }))
    .reverse();
}

// ── Tab wrapper ───────────────────────────────────────────────────────────────
function TabContent({ loading, error, onRetry, children, message }) {
  if (loading) return <PageLoader message={message} />;
  if (error) return <ErrorAlert message={error} onRetry={onRetry} />;
  return children;
}

// ── Kartu grafik trend (sesuai permintaan: pola longitudinal, bukan cuma
// rata-rata/1 data terakhir) — dipakai untuk Sleep, Mood, EPDS, dan
// aktivitas dari smartwatch, semuanya dari /admin/users/{id}/trends ────────
const TREND_STYLE = {
  membaik: { label: "Membaik", className: "bg-green-100 text-green-700" },
  menurun: { label: "Menurun", className: "bg-red-100 text-red-700" },
  stabil: { label: "Stabil", className: "bg-gray-100 text-gray-600" },
};
const CONSISTENCY_STYLE = {
  konsisten: { label: "Konsisten", className: "bg-blue-100 text-blue-700" },
  bervariasi: {
    label: "Bervariasi",
    className: "bg-yellow-100 text-yellow-700",
  },
};

function TrendChartCard({
  icon: Icon,
  title,
  color,
  data,
  dataKey,
  unit = "",
  yDomain,
  summary,
  trendKey,
  trendIsBadWhenUp = false,
  avgLabel,
  emptyHint,
}) {
  const trendValue = summary?.[trendKey];
  // Untuk EPDS, skor naik itu memburuk — badge "Membaik"/"Menurun" perlu dibalik maknanya
  const trendMeta = trendValue
    ? trendIsBadWhenUp
      ? trendValue === "membaik"
        ? TREND_STYLE.menurun
        : trendValue === "menurun"
          ? TREND_STYLE.membaik
          : TREND_STYLE.stabil
      : TREND_STYLE[trendValue]
    : null;
  const consistencyMeta = summary?.consistency
    ? CONSISTENCY_STYLE[summary.consistency]
    : null;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5" style={{ color }} />
          <p className="font-display font-700 text-gray-900">{title}</p>
        </div>
        <div className="flex items-center gap-2">
          {trendMeta && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${trendMeta.className}`}
            >
              {trendMeta.label}
            </span>
          )}
          {consistencyMeta && (
            <span
              className={`text-xs font-semibold px-2 py-0.5 rounded-full ${consistencyMeta.className}`}
            >
              {consistencyMeta.label}
            </span>
          )}
        </div>
      </div>
      {avgLabel && <p className="text-sm text-gray-500 mb-3">{avgLabel}</p>}

      {data.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          Belum ada data pada periode ini
          {emptyHint && (
            <p className="text-xs text-gray-300 mt-1 max-w-sm mx-auto">
              {emptyHint}
            </p>
          )}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11 }}
              tickFormatter={(d) =>
                new Date(d).toLocaleDateString("id-ID", {
                  day: "2-digit",
                  month: "short",
                })
              }
            />
            <YAxis
              domain={yDomain ?? ["auto", "auto"]}
              tick={{ fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                borderRadius: "12px",
                border: "1px solid #e5e7eb",
                fontSize: 12,
              }}
              labelFormatter={(d) =>
                new Date(d).toLocaleDateString("id-ID", {
                  weekday: "short",
                  day: "2-digit",
                  month: "short",
                })
              }
              formatter={(val) => [`${val}${unit}`]}
            />
            <Line
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2.5}
              dot={{ r: 3 }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function UserDetail({
  user: initialUser,
  onBack,
  onSendMessage,
}) {
  const [tab, setTab] = useState("trends");

  // ── State per endpoint ────────────────────────────────────────────────────
  const [profile, setProfile] = useState(null);
  const [healthData, setHealthData] = useState([]);
  const [moodData, setMoodData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [riskData, setRiskData] = useState([]);
  const [epdsData, setEpdsData] = useState([]);
  const [trends, setTrends] = useState(null);
  const [trendPeriod, setTrendPeriod] = useState(7);

  const [profileLoading, setProfileLoading] = useState(true);
  const [healthLoading, setHealthLoading] = useState(true);
  const [moodLoading, setMoodLoading] = useState(true);
  const [sleepLoading, setSleepLoading] = useState(true);
  const [riskLoading, setRiskLoading] = useState(true);
  const [epdsLoading, setEpdsLoading] = useState(true);
  const [trendsLoading, setTrendsLoading] = useState(true);

  const [profileError, setProfileError] = useState("");
  const [healthError, setHealthError] = useState("");
  const [healthFormOpen, setHealthFormOpen] = useState(false);
  const [healthFormSaving, setHealthFormSaving] = useState(false);
  const [healthFormError, setHealthFormError] = useState("");
  const [healthForm, setHealthForm] = useState({
    bpm: "",
    systolic: "",
    diastolic: "",
    steps: "",
    recorded_at: "",
  });
  const [moodError, setMoodError] = useState("");
  const [sleepError, setSleepError] = useState("");
  const [riskError, setRiskError] = useState("");
  const [epdsError, setEpdsError] = useState("");
  const [trendsError, setTrendsError] = useState("");

  const id = initialUser.id;

  // ── Fetch helpers ─────────────────────────────────────────────────────────
  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    setProfileError("");
    try {
      const res = await usersApi.getById(id);
      setProfile(res.data?.data ?? res.data);
    } catch (err) {
      setProfileError(err.response?.data?.message || "Gagal memuat profil");
    } finally {
      setProfileLoading(false);
    }
  }, [id]);

  const fetchHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError("");
    try {
      const res = await usersApi.getHealthData(id);
      setHealthData(res.data?.data ?? []);
    } catch (err) {
      setHealthError(
        err.response?.data?.message || "Gagal memuat data kesehatan",
      );
    } finally {
      setHealthLoading(false);
    }
  }, [id]);

  const handleSubmitHealthForm = async (e) => {
    e.preventDefault();
    setHealthFormError("");

    const payload = {};
    ["bpm", "systolic", "diastolic", "steps"].forEach((k) => {
      if (healthForm[k] !== "") payload[k] = Number(healthForm[k]);
    });
    if (healthForm.recorded_at) payload.recorded_at = healthForm.recorded_at;

    if (Object.keys(payload).length === 0) {
      setHealthFormError(
        "Isi minimal salah satu: nadi, tekanan darah, atau langkah",
      );
      return;
    }

    setHealthFormSaving(true);
    try {
      await usersApi.addHealthData(id, payload);
      setHealthForm({
        bpm: "",
        systolic: "",
        diastolic: "",
        steps: "",
        recorded_at: "",
      });
      setHealthFormOpen(false);
      fetchHealth();
    } catch (err) {
      setHealthFormError(
        err.response?.data?.message || "Gagal menyimpan data kesehatan",
      );
    } finally {
      setHealthFormSaving(false);
    }
  };


  const fetchMood = useCallback(async () => {
    setMoodLoading(true);
    setMoodError("");
    try {
      const res = await usersApi.getMood(id);
      setMoodData(res.data?.data ?? []);
    } catch (err) {
      setMoodError(err.response?.data?.message || "Gagal memuat data mood");
    } finally {
      setMoodLoading(false);
    }
  }, [id]);

  const fetchSleep = useCallback(async () => {
    setSleepLoading(true);
    setSleepError("");
    try {
      const res = await usersApi.getSleep(id);
      setSleepData(res.data?.data ?? []);
    } catch (err) {
      setSleepError(err.response?.data?.message || "Gagal memuat data tidur");
    } finally {
      setSleepLoading(false);
    }
  }, [id]);

  const fetchRisk = useCallback(async () => {
    setRiskLoading(true);
    setRiskError("");
    try {
      const res = await usersApi.getRiskPrediction(id);
      setRiskData(res.data?.data ?? []);
    } catch (err) {
      setRiskError(
        err.response?.data?.message || "Gagal memuat prediksi risiko",
      );
    } finally {
      setRiskLoading(false);
    }
  }, [id]);

  const fetchEpds = useCallback(async () => {
    setEpdsLoading(true);
    setEpdsError("");
    try {
      const res = await usersApi.getQuestionnaireResults(id);
      setEpdsData(res.data?.data ?? []);
    } catch (err) {
      setEpdsError(
        err.response?.data?.message || "Gagal memuat hasil kuesioner",
      );
    } finally {
      setEpdsLoading(false);
    }
  }, [id]);

  const fetchTrends = useCallback(
    async (period = trendPeriod) => {
      setTrendsLoading(true);
      setTrendsError("");
      try {
        const res = await usersApi.getTrends(id, period);
        setTrends(res.data?.data ?? null);
      } catch (err) {
        setTrendsError(
          err.response?.data?.message || "Gagal memuat data tren",
        );
      } finally {
        setTrendsLoading(false);
      }
    },
    [id, trendPeriod],
  );

  useEffect(() => {
    fetchProfile();
    fetchHealth();
    fetchMood();
    fetchSleep();
    fetchRisk();
    fetchEpds();
  }, [fetchProfile, fetchHealth, fetchMood, fetchSleep, fetchRisk, fetchEpds]);

  useEffect(() => {
    fetchTrends(trendPeriod);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendPeriod, id]);

  // ── Derived data ──────────────────────────────────────────────────────────
  const user = profile || initialUser;
  const chartData = groupHealthByDate(healthData);

  // *** DIUBAH 2026-09-17: dulu 1 baris health_data selalu diisi 4 field
  // sekaligus (bpm/systolic/diastolic/steps bareng), jadi cukup ambil baris
  // paling atas (healthData[0]) buat semua kartu. Sekarang smartwatch/sync
  // nulis 1 SENSOR per baris (bpm-only, steps-only, dst — lihat
  // SmartwatchController@sync di backend), jadi baris terbaru bisa saja
  // null di field yang lain. Cari baris terbaru YANG PUNYA NILAI untuk
  // masing-masing metrik, jangan cuma pakai healthData[0]. ***
  const latestBpm =
    healthData.find((h) => h.bpm !== null && h.bpm !== undefined) ?? null;
  const latestBp =
    healthData.find(
      (h) =>
        h.systolic !== null &&
        h.systolic !== undefined &&
        h.diastolic !== null &&
        h.diastolic !== undefined
    ) ?? null;
  const latestSteps =
    healthData.find((h) => h.steps !== null && h.steps !== undefined) ?? null;

  const latestRisk = profile?.latest_risk_prediction ?? riskData[0] ?? null;
  const riskMeta = RISK_LEVEL_MAP[latestRisk?.risk_level] ?? RISK_LEVEL_MAP.low;
  const RiskIcon = riskMeta.icon;

  const avgSleep = sleepData.length
    ? (
        sleepData.reduce((s, d) => s + parseFloat(d.duration_hours), 0) /
        sleepData.length
      ).toFixed(1)
    : "—";

  const TABS = [
    { id: "trends", label: "Trend & Pola", icon: TrendingUp },
    { id: "health", label: "Data Kesehatan", icon: Heart },
    { id: "epds", label: "Hasil Kuesioner", icon: ClipboardList },
    { id: "mood", label: "Mood", icon: Smile },
    { id: "sleep", label: "Tidur", icon: Moon },
    { id: "recs", label: "Rekomendasi Klinis", icon: Stethoscope },
    { id: "csq", label: "Hasil CSQ-8", icon: ClipboardList },
  ];

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={onBack}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h2 className="font-display font-800 text-xl text-gray-900">
            Detail Pengguna
          </h2>
          <p className="text-sm text-gray-400">
            Data kesehatan dan riwayat kuesioner
          </p>
        </div>
      </div>

      {/* Profile card */}
      {profileLoading ? (
        <div className="mb-5">
          <PageLoader message="Memuat profil..." />
        </div>
      ) : profileError ? (
        <div className="mb-5">
          <InlineError message={profileError} />
        </div>
      ) : (
        <Card className="p-5 mb-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-4">
            <Avatar
              initials={(user.name || "U").slice(0, 2).toUpperCase()}
              size="lg"
            />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <h3 className="font-display font-800 text-lg text-gray-900">
                  {user.name}
                </h3>
                {latestRisk && (
                  <span
                    className={`text-xs font-bold px-2.5 py-1 rounded-full border ${riskMeta.bg} ${riskMeta.color} ${riskMeta.border}`}
                  >
                    {riskMeta.label}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-3 text-sm text-gray-500">
                {user.phone && (
                  <span className="flex items-center gap-1">
                    <Phone className="w-3.5 h-3.5" />
                    {user.phone}
                  </span>
                )}
                {user.doctor_name && (
                  <span className="flex items-center gap-1">
                    <User className="w-3.5 h-3.5" />
                    {user.doctor_name}
                  </span>
                )}
                {user.hospital_name && (
                  <span className="flex items-center gap-1">
                    <Building2 className="w-3.5 h-3.5" />
                    {user.hospital_name}
                  </span>
                )}
                {user.delivery_date && (
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    Melahirkan:{" "}
                    {new Date(user.delivery_date).toLocaleDateString("id-ID", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                    })}
                    {user.nifas_day && (
                      <span className="text-primary-600 font-semibold">
                        ({user.nifas_day} hari nifas)
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={() => onSendMessage(user)}
              className="flex items-center gap-2 px-4 py-2 bg-primary-800 text-white text-sm font-semibold rounded-xl hover:bg-primary-900 transition-colors flex-shrink-0"
            >
              <MessageSquare className="w-4 h-4" /> Kirim Pesan
            </button>
          </div>

          {/* Extra info row */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-4 border-t border-gray-100">
            {[
              {
                label: "Usia",
                value: user.age ? `${user.age} tahun` : "—",
                icon: User,
              },
              {
                label: "Anak Ke",
                value: user.child_number ?? "—",
                icon: Users,
              },
              {
                label: "Gol. Darah",
                value: user.blood_type || "—",
                icon: Droplet,
              },
              {
                label: "Berat Badan",
                value: user.weight ? `${parseFloat(user.weight)} kg` : "—",
                icon: Weight,
              },
              {
                label: "Tinggi Badan",
                value: user.height ? `${parseFloat(user.height)} cm` : "—",
                icon: Ruler,
              },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                  <s.icon className="w-3.5 h-3.5 text-gray-500" />
                </div>
                <div>
                  <p className="text-xs text-gray-400">{s.label}</p>
                  <p className="text-sm font-semibold text-gray-800">
                    {s.value}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          {
            label: "BPM Terkini",
            value: latestBpm ? `${latestBpm.bpm} bpm` : "—",
            sub: latestBpm?.pulse_category,
            icon: Heart,
            color: "text-red-500",
            bg: "bg-red-50",
          },
          {
            label: "Tek. Darah",
            value: latestBp
              ? `${latestBp.systolic}/${latestBp.diastolic}`
              : "—",
            sub: latestBp?.bp_category,
            icon: Droplets,
            color: "text-primary-700",
            bg: "bg-primary-50",
          },
          {
            label: "Langkah/Hari",
            value: latestSteps ? latestSteps.steps.toLocaleString() : "—",
            sub: latestSteps?.steps_category,
            icon: Footprints,
            color: "text-green-600",
            bg: "bg-green-50",
          },
          {
            label: "Rata-rata Tidur",
            value: avgSleep !== "—" ? `${avgSleep} jam` : "—",
            icon: Moon,
            color: "text-indigo-600",
            bg: "bg-indigo-50",
          },
        ].map((s) => (
          <Card key={s.label} className="p-4 flex items-center gap-3">
            <div
              className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center flex-shrink-0`}
            >
              <s.icon className={`w-4 h-4 ${s.color}`} />
            </div>
            <div>
              <p className="text-xs text-gray-400">{s.label}</p>
              <p className={`font-display font-800 text-sm ${s.color}`}>
                {s.value}
              </p>
              {s.sub && (
                <p className="text-[10px] text-gray-400 mt-0.5">{s.sub}</p>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-xl mb-5 w-fit">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${tab === t.id ? "bg-white shadow-sm text-primary-800" : "text-gray-500 hover:text-gray-700"}`}
          >
            <t.icon className="w-4 h-4" />
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Trend & Pola (longitudinal, bukan cuma rata-rata/1 hari) ──── */}
      {tab === "trends" && (
        <TabContent>
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <p className="text-sm text-gray-500">
              Pola perubahan {trendPeriod} hari terakhir — dipakai juga oleh
              AI untuk membaca kecenderungan, bukan cuma data satu hari.
            </p>
            <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
              {[7, 14, 30].map((p) => (
                <button
                  key={p}
                  onClick={() => setTrendPeriod(p)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all
                    ${trendPeriod === p ? "bg-white shadow-sm text-primary-800" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {p} hari
                </button>
              ))}
            </div>
          </div>

          {trendsLoading ? (
            <PageLoader message="Memuat data tren..." />
          ) : trendsError ? (
            <ErrorAlert message={trendsError} onRetry={() => fetchTrends()} />
          ) : (
            <div className="space-y-5">
              <TrendChartCard
                icon={Moon}
                title="Durasi Tidur"
                color="#6366f1"
                data={trends?.sleep_trend ?? []}
                dataKey="duration_hours"
                unit=" jam"
                yDomain={[0, 12]}
                summary={trends?.sleep_summary}
                trendKey="duration_trend"
                avgLabel={`${trends?.sleep_summary?.avg_duration_hours ?? "—"} jam`}
              />

              <TrendChartCard
                icon={Smile}
                title="Skor Mood"
                color="#f59e0b"
                data={trends?.mood_trend ?? []}
                dataKey="score"
                unit="/5"
                yDomain={[1, 5]}
                summary={trends?.mood_summary}
                trendKey="mood_trend"
                avgLabel={`${trends?.mood_summary?.avg_score ?? "—"} / 5`}
              />

              <TrendChartCard
                icon={ClipboardList}
                title="Skor EPDS"
                color="#ef4444"
                data={(trends?.epds_trend ?? []).map((e) => ({
                  date: e.date,
                  score: e.total_score,
                }))}
                dataKey="score"
                unit={` / ${trends?.epds_trend?.[0]?.max_score ?? 30}`}
                yDomain={[0, 30]}
                summary={trends?.epds_summary}
                trendKey="score_trend"
                /* Backend sudah mengirim label bermakna klinis untuk EPDS (skor
                   turun = "membaik"), jadi tidak perlu dibalik lagi di sini. */
                avgLabel={
                  trends?.epds_summary?.latest_score != null
                    ? `Terakhir: ${trends.epds_summary.latest_score} (${trends.epds_summary.latest_risk_level ?? "-"})`
                    : "Belum ada data"
                }
                emptyHint="EPDS biasanya diisi berkala, bukan tiap hari — grafik menampilkan submission dalam rentang yang lebih panjang."
              />

              <TrendChartCard
                icon={Footprints}
                title="Langkah Harian"
                color="#22c55e"
                data={trends?.health_trend ?? []}
                dataKey="total_steps"
                unit=" langkah"
                summary={trends?.health_summary}
                trendKey="steps_trend"
                avgLabel={`${trends?.health_summary?.avg_steps ?? "—"} langkah/hari`}
              />
            </div>
          )}
        </TabContent>
      )}

      {/* ── Tab: Data Kesehatan ─────────────────────────────────────────────── */}
      {tab === "health" && (
        <TabContent
          loading={healthLoading}
          error={healthError}
          onRetry={fetchHealth}
          message="Memuat data kesehatan..."
        >
          <div className="space-y-5">
            {/* Input manual — dipakai kalau smartwatch pasien belum konek/terbatas */}
            <Card className="p-5">
              <div className="flex items-center justify-between">
                <SectionTitle>Input Manual</SectionTitle>
                <button
                  onClick={() => setHealthFormOpen((v) => !v)}
                  className="text-sm font-semibold text-primary-700 hover:text-primary-900"
                >
                  {healthFormOpen ? "Batal" : "+ Tambah Data"}
                </button>
              </div>

              {healthFormOpen && (
                <form onSubmit={handleSubmitHealthForm} className="mt-4">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Nadi (bpm)
                      </label>
                      <input
                        type="number"
                        value={healthForm.bpm}
                        onChange={(e) =>
                          setHealthForm((f) => ({ ...f, bpm: e.target.value }))
                        }
                        placeholder="mis. 78"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Sistolik
                      </label>
                      <input
                        type="number"
                        value={healthForm.systolic}
                        onChange={(e) =>
                          setHealthForm((f) => ({
                            ...f,
                            systolic: e.target.value,
                          }))
                        }
                        placeholder="mis. 118"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Diastolik
                      </label>
                      <input
                        type="number"
                        value={healthForm.diastolic}
                        onChange={(e) =>
                          setHealthForm((f) => ({
                            ...f,
                            diastolic: e.target.value,
                          }))
                        }
                        placeholder="mis. 76"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Langkah
                      </label>
                      <input
                        type="number"
                        value={healthForm.steps}
                        onChange={(e) =>
                          setHealthForm((f) => ({
                            ...f,
                            steps: e.target.value,
                          }))
                        }
                        placeholder="mis. 320"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                  </div>

                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Waktu Pencatatan (opsional — kosongkan untuk sekarang)
                    </label>
                    <input
                      type="datetime-local"
                      value={healthForm.recorded_at}
                      onChange={(e) =>
                        setHealthForm((f) => ({
                          ...f,
                          recorded_at: e.target.value,
                        }))
                      }
                      className="w-full sm:w-64 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>

                  <InlineError message={healthFormError} />

                  <button
                    type="submit"
                    disabled={healthFormSaving}
                    className="mt-3 px-5 py-2 bg-primary-800 hover:bg-primary-900 disabled:opacity-60 text-white text-sm font-semibold rounded-xl transition-colors"
                  >
                    {healthFormSaving ? "Menyimpan..." : "Simpan"}
                  </button>
                </form>
              )}
            </Card>

            {chartData.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">
                Belum ada data kesehatan
              </div>
            ) : (
              <>
                <Card className="p-5">
                  <SectionTitle>Detak Jantung (BPM)</SectionTitle>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[50, 120]} tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="bpm"
                        stroke="#1565C0"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: "#1565C0" }}
                        name="BPM"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-5">
                  <SectionTitle>Tekanan Darah</SectionTitle>
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis domain={[50, 160]} tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      <Line
                        type="monotone"
                        dataKey="systolic"
                        stroke="#F57C00"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: "#F57C00" }}
                        name="Sistolik"
                      />
                      <Line
                        type="monotone"
                        dataKey="diastolic"
                        stroke="#1565C0"
                        strokeWidth={2.5}
                        dot={{ r: 3, fill: "#1565C0" }}
                        name="Diastolik"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-5">
                  <SectionTitle>
                    Aktivitas Fisik (Langkah per Hari)
                  </SectionTitle>
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                      />
                      <Bar
                        dataKey="steps"
                        fill="#1565C0"
                        radius={[6, 6, 0, 0]}
                        name="Langkah"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </>
            )}
          </div>
        </TabContent>
      )}

      {/* ── Tab: Hasil Kuesioner ─────────────────────────────────────────────── */}
      {tab === "epds" && (
        <TabContent
          loading={epdsLoading}
          error={epdsError}
          onRetry={fetchEpds}
          message="Memuat hasil kuesioner..."
        >
          {epdsData.length === 0 ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              Belum ada hasil kuesioner
            </div>
          ) : (
            <div className="space-y-4">
              {epdsData.map((result) => (
                <Card key={result.id} className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="font-display font-700 text-gray-900">
                        {result.questionnaire.title}
                      </h3>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {result.questionnaire.description}
                      </p>
                    </div>
                    <span className="text-xs text-gray-400">
                      {new Date(result.submitted_at).toLocaleDateString(
                        "id-ID",
                        { day: "numeric", month: "long", year: "numeric" },
                      )}
                    </span>
                  </div>

                  <div className="flex items-end gap-2 mb-3">
                    <span className="font-display font-900 text-3xl text-primary-800">
                      {result.total_score}
                    </span>
                    <span className="text-gray-400 text-sm mb-1">
                      / {result.max_score}
                    </span>
                    <span className="text-sm text-gray-500 mb-1">
                      ({result.percentage}%)
                    </span>
                    {result.epds_risk_level && (
                      <span
                        className="text-xs font-bold px-2.5 py-1 rounded-full mb-1 ml-1"
                        style={{
                          color:
                            result.epds_risk_color === "red"
                              ? "#dc2626"
                              : result.epds_risk_color === "yellow"
                                ? "#d97706"
                                : "#16a34a",
                          background:
                            result.epds_risk_color === "red"
                              ? "#fef2f2"
                              : result.epds_risk_color === "yellow"
                                ? "#fffbeb"
                                : "#f0fdf4",
                        }}
                      >
                        {result.epds_risk_level}
                      </span>
                    )}
                  </div>

                  <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${result.percentage}%`,
                        background: result.epds_risk_level
                          ? result.epds_risk_color === "red"
                            ? "#dc2626"
                            : result.epds_risk_color === "yellow"
                              ? "#d97706"
                              : "#16a34a"
                          : result.percentage <= 33
                            ? "#16a34a"
                            : result.percentage <= 60
                              ? "#d97706"
                              : "#dc2626",
                      }}
                    />
                  </div>
                  {result.questionnaire?.category === "epds" && (
                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                      <span>Normal (0-9)</span>
                      <span>Sedang (10-12)</span>
                      <span>Berat (≥13)</span>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          )}
        </TabContent>
      )}

      {/* ── Tab: Mood ───────────────────────────────────────────────────────── */}
      {tab === "mood" && (
        <TabContent
          loading={moodLoading}
          error={moodError}
          onRetry={fetchMood}
          message="Memuat data mood..."
        >
          <div className="space-y-5">
            {/* Mood timeline */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Smile className="w-5 h-5 text-primary-700" />
                <SectionTitle>Mood Harian</SectionTitle>
              </div>

              {moodData.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Belum ada data mood
                </div>
              ) : (
                <>
                  {/* Timeline emoji */}
                  <div className="flex items-end justify-start gap-3 mb-6 overflow-x-auto pb-2">
                    {[...moodData].reverse().map((entry, i) => {
                      const m = MOOD_MAP[entry.mood] ?? MOOD_MAP.biasa_saja;
                      return (
                        <div
                          key={entry.id}
                          className="flex flex-col items-center gap-1.5 flex-shrink-0"
                        >
                          <div
                            className={`w-11 h-11 rounded-full ${m.bg} ${m.border} border-2 flex items-center justify-center text-xl`}
                          >
                            {m.emoji}
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(entry.date).toLocaleDateString("id-ID", {
                              day: "numeric",
                              month: "short",
                            })}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* Mood list */}
                  <div className="space-y-2">
                    {moodData.map((entry) => {
                      const m = MOOD_MAP[entry.mood] ?? MOOD_MAP.biasa_saja;
                      return (
                        <div
                          key={entry.id}
                          className={`flex items-start gap-3 p-3 rounded-xl ${m.bg}`}
                        >
                          <span className="text-2xl flex-shrink-0">
                            {m.emoji}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className={`text-xs font-bold ${m.color}`}>
                                {m.label}
                              </span>
                              <span className="text-xs text-gray-400">
                                {new Date(entry.date).toLocaleDateString(
                                  "id-ID",
                                  {
                                    weekday: "short",
                                    day: "numeric",
                                    month: "short",
                                  },
                                )}
                              </span>
                            </div>
                            {entry.note && (
                              <p className="text-sm text-gray-600">
                                {entry.note}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabContent>
      )}

      {/* ── Tab: Tidur ──────────────────────────────────────────────────────── */}
      {tab === "sleep" && (
        <TabContent
          loading={sleepLoading}
          error={sleepError}
          onRetry={fetchSleep}
          message="Memuat data tidur..."
        >
          <div className="space-y-5">
            {/* Sleep data */}
            <Card className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <Moon className="w-5 h-5 text-indigo-600" />
                <SectionTitle>Data Tidur</SectionTitle>
              </div>

              {sleepData.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  Belum ada data tidur
                </div>
              ) : (
                <>
                  {/* Sleep chart */}
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={[...sleepData].reverse()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11 }}
                        tickFormatter={(d) =>
                          new Date(d).toLocaleDateString("id-ID", {
                            day: "2-digit",
                            month: "short",
                          })
                        }
                      />
                      <YAxis
                        domain={[0, 12]}
                        tick={{ fontSize: 11 }}
                        tickFormatter={(v) => `${v}j`}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: "12px",
                          border: "1px solid #e5e7eb",
                          fontSize: 12,
                        }}
                        formatter={(val) => [`${val} jam`]}
                      />
                      <Bar
                        dataKey="duration_hours"
                        fill="#6366f1"
                        radius={[6, 6, 0, 0]}
                        name="Durasi Tidur"
                      />
                    </BarChart>
                  </ResponsiveContainer>

                  {/* Sleep summary */}
                  <div className="grid grid-cols-3 gap-3 mt-4 mb-5">
                    {[
                      { label: "Rata-rata", value: `${avgSleep} jam` },
                      {
                        label: "Terlama",
                        value: `${Math.max(...sleepData.map((d) => parseFloat(d.duration_hours)))} jam`,
                      },
                      {
                        label: "Terpendek",
                        value: `${Math.min(...sleepData.map((d) => parseFloat(d.duration_hours)))} jam`,
                      },
                    ].map((s) => (
                      <div
                        key={s.label}
                        className="bg-indigo-50 rounded-xl p-3 text-center"
                      >
                        <p className="font-display font-700 text-indigo-700 text-base">
                          {s.value}
                        </p>
                        <p className="text-xs text-indigo-500 mt-0.5">
                          {s.label}
                        </p>
                      </div>
                    ))}
                  </div>

                  {/* Sleep list */}
                  <div className="space-y-2">
                    {sleepData.map((entry) => {
                      const q = QUALITY_MAP[entry.quality] ?? QUALITY_MAP[1];
                      return (
                        <div
                          key={entry.id}
                          className="p-3 rounded-xl bg-gray-50 border border-gray-100"
                        >
                          <div className="flex items-center justify-between mb-1.5">
                            <span className="text-sm font-semibold text-gray-900">
                              {new Date(entry.date).toLocaleDateString(
                                "id-ID",
                                {
                                  weekday: "long",
                                  day: "numeric",
                                  month: "long",
                                },
                              )}
                            </span>
                            <span
                              className={`text-xs font-semibold ${
                                SLEEP_CATEGORY_COLOR[entry.sleep_category] ??
                                q.color
                              }`}
                            >
                              {entry.sleep_category ?? q.label}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-gray-500">
                            <span>
                              🛏 {entry.bed_time?.slice(0, 5)} →{" "}
                              {entry.wake_time?.slice(0, 5)}
                            </span>
                            <span>⏱ {entry.duration_hours} jam</span>
                            <span>😴 Kualitas: {q.label}</span>
                            <span>
                              ⚠ Terbangun{" "}
                              {WAKEUP_MAP[entry.wakeup_count] ?? "-"}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </Card>
          </div>
        </TabContent>
      )}

      {/* ── Tab: Rekomendasi Klinis ──────────────────────────────────────────── */}
      {tab === "recs" && (
        <TabContent
          loading={riskLoading}
          error={riskError}
          onRetry={fetchRisk}
          message="Memuat rekomendasi..."
        >
          {!latestRisk ? (
            <div className="text-center py-12 text-gray-400 text-sm">
              Belum ada data prediksi risiko
            </div>
          ) : (
            <div className="space-y-4">
              {/* Risk summary card */}
              <Card
                className={`p-5 border-l-4 ${riskMeta.border.replace("border", "border-l")}`}
              >
                <div className="flex items-start gap-4">
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 ${riskMeta.bg}`}
                  >
                    <RiskIcon className={`w-7 h-7 ${riskMeta.color}`} />
                  </div>
                  <div className="flex-1">
                    <div
                      className={`font-display font-800 text-xl mb-1 ${riskMeta.color}`}
                    >
                      {riskMeta.label}
                    </div>
                    {latestRisk.summary && (
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {latestRisk.summary}
                      </p>
                    )}
                    <p className="text-xs text-gray-400 mt-2">
                      Diprediksi:{" "}
                      {new Date(latestRisk.predicted_at).toLocaleString(
                        "id-ID",
                        {
                          day: "numeric",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        },
                      )}
                    </p>
                  </div>
                  <button
                    onClick={() => onSendMessage(user)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-800 text-white text-sm font-semibold rounded-xl hover:bg-primary-900 transition-colors flex-shrink-0"
                  >
                    <MessageSquare className="w-4 h-4" /> Kirim Pesan
                  </button>
                </div>

                {/* Score breakdown */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-gray-100">
                  {[
                    { label: "EPDS Score", value: latestRisk.epds_score },
                    { label: "Mood Score", value: latestRisk.mood_score },
                    { label: "Sleep Score", value: latestRisk.sleep_score },
                    { label: "BPM Score", value: latestRisk.bpm_score },
                    {
                      label: "Activity Score",
                      value: latestRisk.activity_score,
                    },
                  ]
                    .filter((s) => s.value !== null)
                    .map((s) => (
                      <div key={s.label} className="bg-gray-50 rounded-xl p-3">
                        <p className="text-xs text-gray-400 mb-1">{s.label}</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${riskMeta.barColor}`}
                              style={{ width: `${s.value}%` }}
                            />
                          </div>
                          <span className="text-xs font-bold text-gray-700">
                            {s.value}
                          </span>
                        </div>
                      </div>
                    ))}
                </div>
              </Card>

              {/* Kesimpulan klinis (1 kartu).
                  Prediksi baru dari AI sudah berisi 1 kesimpulan. Prediksi lama
                  yang tersimpan sebagai beberapa poin digabung jadi satu
                  paragraf (urut prioritas tertinggi) supaya tampilannya sama. */}
              {(() => {
                const recs = Array.isArray(latestRisk.recommendation)
                  ? latestRisk.recommendation.filter((r) => r?.action)
                  : [];
                if (recs.length === 0) return null;

                const order = { high: 0, medium: 1, low: 2 };
                const sorted = [...recs].sort(
                  (x, y) => (order[x.priority] ?? 2) - (order[y.priority] ?? 2),
                );
                const conclusion = sorted
                  .map((r) => r.action.trim())
                  .map((t) => (/[.!?]$/.test(t) ? t : `${t}.`))
                  .join(" ");
                const priority = sorted[0].priority;

                return (
                  <div>
                    <h3 className="font-display font-700 text-gray-900 mb-3">
                      Kesimpulan
                    </h3>
                    <Card className="p-4">
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 ${riskMeta.bg} ${riskMeta.color}`}
                        >
                          <Stethoscope className="w-4 h-4" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-gray-700 leading-relaxed">
                            {conclusion}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-bold px-2.5 py-0.5 rounded-full border flex-shrink-0 ${PRIORITY_COLORS[priority] || PRIORITY_COLORS.low}`}
                        >
                          {priority === "high"
                            ? "Tinggi"
                            : priority === "medium"
                              ? "Sedang"
                              : "Rendah"}
                        </span>
                      </div>
                    </Card>
                  </div>
                );
              })()}

              {/* Riwayat prediksi */}
              {riskData.length > 1 && (
                <div>
                  <h3 className="font-display font-700 text-gray-900 mb-3">
                    Riwayat Prediksi
                  </h3>
                  <Card className="overflow-hidden">
                    <div className="divide-y divide-gray-50">
                      {riskData.map((r) => {
                        const rm =
                          RISK_LEVEL_MAP[r.risk_level] ?? RISK_LEVEL_MAP.low;
                        return (
                          <div
                            key={r.id}
                            className="flex items-center gap-3 px-4 py-3"
                          >
                            <span
                              className={`text-xs font-bold px-2.5 py-1 rounded-full border ${rm.bg} ${rm.color} ${rm.border}`}
                            >
                              {rm.label}
                            </span>
                            <span className="text-xs text-gray-400 flex-1">
                              {new Date(r.predicted_at).toLocaleString(
                                "id-ID",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 flex items-start gap-3">
                <AlertTriangle className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-gray-500 leading-relaxed">
                  Rekomendasi ini dihasilkan berdasarkan analisis data AI dan{" "}
                  <strong>bukan pengganti penilaian klinis profesional</strong>.
                </p>
              </div>
            </div>
          )}
        </TabContent>
      )}

      {/* ── Tab: Hasil CSQ-8 ─────────────────────────────────────────────── */}
      {tab === "csq" && <CsqResults user={user} />}
    </div>
  );
}
